import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import supabase from '../../api/supabase';
import { toast } from 'react-hot-toast';

const ProjectManagement = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [viewAsCustomer, setViewAsCustomer] = useState(false);

  const statusOptions = ['planning', 'in-progress', 'on-hold', 'completed', 'cancelled'];

  // Enhanced form state for new project
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    customer: '',
    status: 'planning',
    start_date: '',
    location_address: '',
    location_city: '',
    location_state: '',
    location_zip_code: '',
    budget_estimated: '',
    progress: 0,
    phases: [],
    payments: [],
    building_type: 'Ground Floor',
    timeline_entries: [],
    customer_visible: true,
    internal_notes: ''
  });

  // Form state for editing project
  const [editProject, setEditProject] = useState({});
  
  // Form state for phases
  const [newPhase, setNewPhase] = useState({ name: '', status: 'pending', start_date: '', end_date: '', progress: 0 });

  // Form state for timeline entries
  const [newTimelineEntry, setNewTimelineEntry] = useState({
    title: '',
    description: '',
    date: '',
    type: 'milestone',
    customer_visible: true,
    attachments: []
  });

  // Add state for new payment
  const [newPayment, setNewPayment] = useState({ phase: '', amount: '', status: 'pending', due_date: '', paid_date: '', payment_method: 'bank-transfer' });

  // Add state for new activity log entry
  const [newActivity, setNewActivity] = useState({ title: '', description: '', date: '', type: 'milestone' });

  // Add state for advanced section toggle
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchCustomers();
    
    // Set up real-time subscription for projects
    const projectsSubscription = supabase
      .channel('projects-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'projects' },
        (payload) => {
          console.log('Projects real-time update:', payload);
          if (payload.eventType === 'INSERT') {
            setProjects(prev => [payload.new, ...prev]);
            toast.success('New project created!');
          } else if (payload.eventType === 'UPDATE') {
            setProjects(prev => prev.map(project => project.id === payload.new.id ? payload.new : project));
            toast.success('Project updated!');
          } else if (payload.eventType === 'DELETE') {
            setProjects(prev => prev.filter(project => project.id !== payload.old.id));
            toast.success('Project deleted!');
          }
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      projectsSubscription.unsubscribe();
    };
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/projects');
      setProjects(response.data.data);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/admin/customers');
      setCustomers(response.data.data);
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    }
  };

  // Utility function to capitalize first letter of every word
  function capitalizeWords(str) {
    return str.replace(/\b\w/g, char => char.toUpperCase());
  }

  // Utility to calculate progress from phases
  function getPhaseProgress(project) {
    if (!project.phases || project.phases.length === 0) return 0;
    const completed = project.phases.filter(p => p.status === 'Completed').length;
    return Math.round((completed / project.phases.length) * 100);
  }

  // Utility to format numbers in Indian system (e.g., 1 crore 30 lakh)
  function formatIndianPrice(num) {
    if (!num) return 'N/A';
    num = Number(num);
    if (isNaN(num)) return num;
    if (num >= 10000000) {
      const crore = Math.floor(num / 10000000);
      const lakh = Math.floor((num % 10000000) / 100000);
      return `${crore} crore${lakh ? ' ' + lakh + ' lakh' : ''}`;
    } else if (num >= 100000) {
      const lakh = Math.floor(num / 100000);
      const thousand = Math.floor((num % 100000) / 1000);
      return `${lakh} lakh${thousand ? ' ' + thousand + ' thousand' : ''}`;
    } else if (num >= 1000) {
      const thousand = Math.floor(num / 1000);
      return `${thousand} thousand`;
    }
    return num.toString();
  }

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      // Remove customer validation - allow empty/null
      // Extract payments from project data to avoid database error
      const paymentsToCreate = newProject.payments || [];
      const projectDataWithoutPayments = { ...newProject };
      delete projectDataWithoutPayments.payments;
      // Capitalize project name
      projectDataWithoutPayments.name = capitalizeWords(projectDataWithoutPayments.name || '');
      // If customer is empty string, set to null
      if (!projectDataWithoutPayments.customer) projectDataWithoutPayments.customer = null;
      console.log('Creating project with data:', projectDataWithoutPayments);
      const response = await api.post('/admin/projects', projectDataWithoutPayments);
      const createdProject = response.data.data;
      
      // Create payments separately if any were added
      if (paymentsToCreate.length > 0) {
        console.log('Creating payments for project:', paymentsToCreate);
        for (const payment of paymentsToCreate) {
          try {
            const paymentData = {
              project: createdProject.id,
              customer: createdProject.customer,
              phase: payment.phase,
              amount: payment.amount,
              status: payment.status,
              due_date: payment.due_date,
              paid_date: payment.paid_date,
              payment_method: payment.payment_method,
              currency: 'INR'
            };
            await api.post('/admin/payments', paymentData);
          } catch (paymentError) {
            console.error('Failed to create payment:', paymentError);
            // Continue with other payments even if one fails
          }
        }
      }

      setProjects(prev => [createdProject, ...prev]);
      setShowAddModal(false);
      setNewProject({
        name: '',
        description: '',
        customer: '',
        status: 'planning',
        start_date: '',
        location_address: '',
        location_city: '',
        location_state: '',
        location_zip_code: '',
        budget_estimated: '',
        progress: 0,
        phases: [],
        payments: [],
        building_type: 'Ground Floor',
        timeline_entries: [],
        customer_visible: true,
        internal_notes: ''
      });
    } catch (err) {
      console.error('Failed to create project:', err);
      alert('Failed to create project. Please try again.');
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      // Remove display-only fields before sending to backend
      const { customer_name, customer_email, customer_phone, ...projectData } = editProject;
      // Capitalize project name
      projectData.name = capitalizeWords(projectData.name || '');
      const response = await api.put(`/admin/projects/${editProject.id}`, projectData);
      setProjects(prev => prev.map(p => p.id === editProject.id ? response.data.data : p));
      setShowEditModal(false);
      setEditProject({});
      setSelectedProject(null);
    } catch (err) {
      console.error('Failed to update project:', err);
      alert('Failed to update project. Please try again.');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/admin/projects/${projectId}`);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
      }
    } catch (err) {
      console.error('Failed to delete project:', err);
      alert('Failed to delete project. Please try again.');
    }
  };

  const handleAddPhase = async (e) => {
    e.preventDefault();
    try {
      const updatedProject = {
        ...selectedProject,
        phases: [...(selectedProject.phases || []), newPhase]
      };
      const response = await api.put(`/admin/projects/${selectedProject.id}`, updatedProject);
      setProjects(prev => prev.map(p => p.id === selectedProject.id ? response.data.data : p));
      setSelectedProject(response.data.data);
      setShowPhaseModal(false);
      setNewPhase({
        name: '',
        status: 'pending',
        start_date: '',
        end_date: '',
        progress: 0
      });
    } catch (err) {
      console.error('Failed to add phase:', err);
      alert('Failed to add phase. Please try again.');
    }
  };

  const handleAddTimelineEntry = async (e) => {
    e.preventDefault();
    try {
      const updatedProject = {
        ...selectedProject,
        timeline_entries: [...(selectedProject.timeline_entries || []), newTimelineEntry]
      };
      const response = await api.put(`/admin/projects/${selectedProject.id}`, updatedProject);
      setProjects(prev => prev.map(p => p.id === selectedProject.id ? response.data.data : p));
      setSelectedProject(response.data.data);
      setShowTimelineModal(false);
      setNewTimelineEntry({
        title: '',
        description: '',
        date: '',
        type: 'milestone',
        customer_visible: true,
        attachments: []
      });
    } catch (err) {
      console.error('Failed to add timeline entry:', err);
      alert('Failed to add timeline entry. Please try again.');
    }
  };

  const toggleCustomerVisibility = async (projectId, visible) => {
    try {
      const project = projects.find(p => p.id === projectId);
      const updatedProject = { ...project, customer_visible: visible };
      const response = await api.put(`/admin/projects/${projectId}`, updatedProject);
      setProjects(prev => prev.map(p => p.id === projectId ? response.data.data : p));
    } catch (err) {
      console.error('Failed to update visibility:', err);
      alert('Failed to update visibility. Please try again.');
    }
  };

  const openEditModal = (project) => {
    setEditProject(project);
    setShowEditModal(true);
  };

  const openPhaseModal = (project) => {
    setSelectedProject(project);
    setShowPhaseModal(true);
  };

  const openTimelineModal = (project) => {
    setSelectedProject(project);
    setShowTimelineModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'planning': 'bg-blue-100 text-blue-800',
      'in-progress': 'bg-green-100 text-green-800',
      'on-hold': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-purple-100 text-purple-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.planning;
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 60) return 'bg-blue-500';
    if (progress >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const filteredProjects = projects.filter(project => {
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.location_address?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-palamuru-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Projects</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-palamuru-gold text-white px-4 py-2 rounded-lg hover:bg-palamuru-gold-dark transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display-bold text-gray-900">Project Management</h1>
          <p className="text-gray-600 mt-1">Manage all construction projects and track their progress.</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="viewAsCustomer"
              checked={viewAsCustomer}
              onChange={(e) => setViewAsCustomer(e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="viewAsCustomer" className="text-sm text-gray-600">
              View as Customer
            </label>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-body-semibold text-sm flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Add New Project</span>
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-2xl shadow-soft border border-gray-100">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-body-semibold text-gray-700 mb-2">Search Projects</label>
            <input
              type="text"
              placeholder="Search by project name, customer, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-body-semibold text-gray-700 mb-2">Filter by Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProjects.map(project => (
          <div key={project.id} className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6 hover:shadow-medium transition-shadow duration-200">
            {/* Project Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="font-display-semibold text-lg text-gray-900 mb-1">{project.name}</h3>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-body-semibold ${getStatusColor(project.status)}`}>
                    {project.status.replace('-', ' ')}
                  </span>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => openEditModal(project)}
                  className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Edit Project"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDeleteProject(project.id)}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  title="Delete Project"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-3 mb-4">
              <div>
                <p className="text-sm text-gray-600">Customer</p>
                <p className="font-medium text-gray-900">{project.customer_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Location</p>
                <p className="font-medium text-gray-900">{project.location_address || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Agreed Price</p>
                <p className="font-medium text-gray-900">₹{formatIndianPrice(project.budget_estimated)}</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Progress</span>
                <span className="text-sm font-medium text-gray-900">{getPhaseProgress(project)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${getProgressColor(getPhaseProgress(project))}`}
                  style={{ width: `${getPhaseProgress(project)}%` }}
                ></div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-2">
              <Link
                to={`/admin/projects/${project.id}`}
                className="flex-1 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium text-center"
              >
                View Details
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* Add Project Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Add New Project</h2>
            <form onSubmit={handleCreateProject} className="space-y-4">
              {/* ESSENTIAL FIELDS */}
              <input type="text" placeholder="Project Name" value={newProject.name} onChange={e => setNewProject({ ...newProject, name: e.target.value })} className="border p-2 rounded w-full mb-2" />
              <select value={newProject.customer} onChange={e => setNewProject({ ...newProject, customer: e.target.value })} className="border p-2 rounded w-full mb-2">
                <option value="">Unassigned (Assign later)</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>{customer.full_name || customer.email}</option>
                ))}
              </select>
              <input type="date" placeholder="Start Date" value={newProject.start_date} onChange={e => setNewProject({ ...newProject, start_date: e.target.value })} className="border p-2 rounded w-full mb-2" />
              <input type="text" placeholder="Address" value={newProject.location_address} onChange={e => setNewProject({ ...newProject, location_address: e.target.value })} className="border p-2 rounded w-full mb-2" />
              <input type="text" placeholder="City" value={newProject.location_city} onChange={e => setNewProject({ ...newProject, location_city: e.target.value })} className="border p-2 rounded w-full mb-2" />
              <input type="text" placeholder="State" value={newProject.location_state} onChange={e => setNewProject({ ...newProject, location_state: e.target.value })} className="border p-2 rounded w-full mb-2" />
              <input type="text" placeholder="Zip Code" value={newProject.location_zip_code} onChange={e => setNewProject({ ...newProject, location_zip_code: e.target.value })} className="border p-2 rounded w-full mb-2" />
              <input type="number" placeholder="Agreed Price (₹)" value={newProject.budget_estimated} onChange={e => setNewProject({ ...newProject, budget_estimated: e.target.value })} className="border p-2 rounded w-full mb-2" required />
              {newProject.budget_estimated && (
                <div className="text-xs text-gray-500 mb-2">In words: {formatIndianPrice(newProject.budget_estimated)}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Building Type</label>
                <select
                  value={newProject.building_type}
                  onChange={e => setNewProject({ ...newProject, building_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="Ground Floor">Ground Floor</option>
                  <option value="G+1">G+1 (Ground + First Floor)</option>
                  <option value="G+2">G+2 (Ground + First + Second Floor)</option>
                </select>
              </div>

              {/* ADVANCED SECTION TOGGLE */}
              <button type="button" className="text-blue-600 underline mb-2" onClick={() => setShowAdvanced(v => !v)}>
                {showAdvanced ? 'Hide Advanced Details' : 'Add More Details'}
              </button>

              {/* ADVANCED FIELDS (collapsible) */}
              {showAdvanced && (
                <div className="border rounded p-2 bg-gray-50 mb-2">
                  {/* PHASES SECTION */}
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2">Phases</h4>
                    <ul>
                      {newProject.phases.map((phase, idx) => (
                        <li key={idx} className="flex items-center mb-1">
                          <span className="flex-1">{phase.name} ({phase.status}) {phase.progress}%</span>
                          <button type="button" onClick={() => setNewProject({ ...newProject, phases: newProject.phases.filter((_, i) => i !== idx) })} className="ml-2 text-red-500">Remove</button>
                        </li>
                      ))}
                    </ul>
                    <div className="flex gap-2 mt-2">
                      <input type="text" placeholder="Name" value={newPhase.name} onChange={e => setNewPhase({ ...newPhase, name: e.target.value })} className="border p-1 rounded" />
                      <input type="text" placeholder="Status" value={newPhase.status} onChange={e => setNewPhase({ ...newPhase, status: e.target.value })} className="border p-1 rounded" />
                      <input type="date" value={newPhase.start_date} onChange={e => setNewPhase({ ...newPhase, start_date: e.target.value })} className="border p-1 rounded" />
                      <input type="date" value={newPhase.end_date} onChange={e => setNewPhase({ ...newPhase, end_date: e.target.value })} className="border p-1 rounded" />
                      <input type="number" placeholder="Progress" value={newPhase.progress} onChange={e => setNewPhase({ ...newPhase, progress: Number(e.target.value) })} className="border p-1 rounded w-16" />
                      <button type="button" onClick={() => { setNewProject({ ...newProject, phases: [...newProject.phases, newPhase] }); setNewPhase({ name: '', status: 'pending', start_date: '', end_date: '', progress: 0 }); }} className="bg-blue-500 text-white px-2 rounded">Add</button>
                    </div>
                  </div>

                  {/* PAYMENTS SECTION */}
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2">Payments</h4>
                    <ul>
                      {newProject.payments?.map((payment, idx) => (
                        <li key={idx} className="flex items-center mb-1">
                          <span className="flex-1">{payment.phase} - ₹{payment.amount} ({payment.status})</span>
                          <button type="button" onClick={() => setNewProject({ ...newProject, payments: newProject.payments.filter((_, i) => i !== idx) })} className="ml-2 text-red-500">Remove</button>
                        </li>
                      ))}
                    </ul>
                    <div className="flex gap-2 mt-2">
                      <input type="text" placeholder="Phase" value={newPayment.phase} onChange={e => setNewPayment({ ...newPayment, phase: e.target.value })} className="border p-1 rounded" />
                      <input type="number" placeholder="Amount" value={newPayment.amount} onChange={e => setNewPayment({ ...newPayment, amount: e.target.value })} className="border p-1 rounded w-20" />
                      <input type="text" placeholder="Status" value={newPayment.status} onChange={e => setNewPayment({ ...newPayment, status: e.target.value })} className="border p-1 rounded" />
                      <input type="date" value={newPayment.due_date} onChange={e => setNewPayment({ ...newPayment, due_date: e.target.value })} className="border p-1 rounded" />
                      <input type="date" value={newPayment.paid_date} onChange={e => setNewPayment({ ...newPayment, paid_date: e.target.value })} className="border p-1 rounded" />
                      <input type="text" placeholder="Method" value={newPayment.payment_method} onChange={e => setNewPayment({ ...newPayment, payment_method: e.target.value })} className="border p-1 rounded" />
                      <button type="button" onClick={() => { setNewProject({ ...newProject, payments: [...(newProject.payments || []), newPayment] }); setNewPayment({ phase: '', amount: '', status: 'pending', due_date: '', paid_date: '', payment_method: 'bank-transfer' }); }} className="bg-blue-500 text-white px-2 rounded">Add</button>
                    </div>
                  </div>

                  {/* ACTIVITY LOG SECTION */}
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2">Activity Log</h4>
                    <ul>
                      {newProject.activity_log?.map((activity, idx) => (
                        <li key={idx} className="flex items-center mb-1">
                          <span className="flex-1">{activity.title} ({activity.type}) {activity.date}</span>
                          <button type="button" onClick={() => setNewProject({ ...newProject, activity_log: newProject.activity_log.filter((_, i) => i !== idx) })} className="ml-2 text-red-500">Remove</button>
                        </li>
                      ))}
                    </ul>
                    <div className="flex gap-2 mt-2">
                      <input type="text" placeholder="Title" value={newActivity.title} onChange={e => setNewActivity({ ...newActivity, title: e.target.value })} className="border p-1 rounded" />
                      <input type="text" placeholder="Type" value={newActivity.type} onChange={e => setNewActivity({ ...newActivity, type: e.target.value })} className="border p-1 rounded" />
                      <input type="date" value={newActivity.date} onChange={e => setNewActivity({ ...newActivity, date: e.target.value })} className="border p-1 rounded" />
                      <input type="text" placeholder="Description" value={newActivity.description} onChange={e => setNewActivity({ ...newActivity, description: e.target.value })} className="border p-1 rounded w-40" />
                      <button type="button" onClick={() => { setNewProject({ ...newProject, activity_log: [...(newProject.activity_log || []), newActivity] }); setNewActivity({ title: '', description: '', date: '', type: 'milestone' }); }} className="bg-blue-500 text-white px-2 rounded">Add</button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Phase Modal */}
      {showPhaseModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Add Phase to {selectedProject.name}</h2>
            <form onSubmit={handleAddPhase} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phase Name</label>
                <input
                  type="text"
                  value={newPhase.name}
                  onChange={(e) => setNewPhase({...newPhase, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newPhase.description}
                  onChange={(e) => setNewPhase({...newPhase, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={newPhase.start_date}
                    onChange={(e) => setNewPhase({...newPhase, start_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={newPhase.end_date}
                    onChange={(e) => setNewPhase({...newPhase, end_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={newPhase.status}
                    onChange={(e) => setNewPhase({...newPhase, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="on-hold">On Hold</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Progress (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={newPhase.progress}
                    onChange={(e) => setNewPhase({...newPhase, progress: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Budget Allocation</label>
                <input
                  type="number"
                  value={newPhase.budget_allocation}
                  onChange={(e) => setNewPhase({...newPhase, budget_allocation: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="₹"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowPhaseModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Phase
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Timeline Entry Modal */}
      {showTimelineModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Add Timeline Entry to {selectedProject.name}</h2>
            <form onSubmit={handleAddTimelineEntry} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={newTimelineEntry.title}
                  onChange={(e) => setNewTimelineEntry({...newTimelineEntry, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newTimelineEntry.description}
                  onChange={(e) => setNewTimelineEntry({...newTimelineEntry, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={newTimelineEntry.date}
                    onChange={(e) => setNewTimelineEntry({...newTimelineEntry, date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={newTimelineEntry.type}
                    onChange={(e) => setNewTimelineEntry({...newTimelineEntry, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="milestone">Milestone</option>
                    <option value="update">Update</option>
                    <option value="issue">Issue</option>
                    <option value="completion">Completion</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="timelineCustomerVisible"
                  checked={newTimelineEntry.customer_visible}
                  onChange={(e) => setNewTimelineEntry({...newTimelineEntry, customer_visible: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <label htmlFor="timelineCustomerVisible" className="text-sm text-gray-700">
                  Visible to Customer
                </label>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowTimelineModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditModal && editProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Edit Project</h2>
            <form onSubmit={handleUpdateProject} className="space-y-4">
              <input type="text" placeholder="Project Name" value={editProject.name || ''} onChange={e => setEditProject({ ...editProject, name: e.target.value })} className="border p-2 rounded w-full mb-2" required />
              <textarea placeholder="Description" value={editProject.description || ''} onChange={e => setEditProject({ ...editProject, description: e.target.value })} className="border p-2 rounded w-full mb-2" />
              <input type="date" placeholder="Start Date" value={editProject.start_date || ''} onChange={e => setEditProject({ ...editProject, start_date: e.target.value })} className="border p-2 rounded w-full mb-2" />
              <input type="text" placeholder="Address" value={editProject.location_address || ''} onChange={e => setEditProject({ ...editProject, location_address: e.target.value })} className="border p-2 rounded w-full mb-2" />
              <input type="text" placeholder="City" value={editProject.location_city || ''} onChange={e => setEditProject({ ...editProject, location_city: e.target.value })} className="border p-2 rounded w-full mb-2" />
              <input type="text" placeholder="State" value={editProject.location_state || ''} onChange={e => setEditProject({ ...editProject, location_state: e.target.value })} className="border p-2 rounded w-full mb-2" />
              <input type="text" placeholder="Zip Code" value={editProject.location_zip_code || ''} onChange={e => setEditProject({ ...editProject, location_zip_code: e.target.value })} className="border p-2 rounded w-full mb-2" />
              <input type="number" placeholder="Budget (optional)" value={editProject.budget_estimated || ''} onChange={e => setEditProject({ ...editProject, budget_estimated: e.target.value })} className="border p-2 rounded w-full mb-2" />
              <div className="flex justify-end space-x-3">
                <button type="button" onClick={() => { setShowEditModal(false); setEditProject({}); }} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Update Project</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManagement; 